import axios from '../config/axios';
import { authService } from './authService';
import { Patient, PatientFormData, PatientVisit, PatientVisitFormData } from '../types/patient';

class PatientService {
  private getAuthHeaders() {
    const token = authService.getToken();
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  // Patient CRUD operations
  async getPatients(): Promise<Patient[]> {
    try {
      const response = await axios.get('/api/scribing/patients/', {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching patients:', error);
      throw error;
    }
  }

  async getPatient(id: number): Promise<Patient> {
    try {
      const response = await axios.get(`/api/scribing/patients/${id}`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching patient:', error);
      throw error;
    }
  }

  async createPatient(patientData: PatientFormData): Promise<Patient> {
    try {
      const response = await axios.post('/api/scribing/patients/', patientData, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Error creating patient:', error);
      throw error;
    }
  }

  async updatePatient(id: number, patientData: Partial<PatientFormData>): Promise<Patient> {
    try {
      const response = await axios.put(`/api/scribing/patients/${id}`, patientData, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Error updating patient:', error);
      throw error;
    }
  }

  async deletePatient(id: number): Promise<void> {
    try {
      await axios.delete(`/api/scribing/patients/${id}`, {
        headers: this.getAuthHeaders(),
      });
    } catch (error) {
      console.error('Error deleting patient:', error);
      throw error;
    }
  }

  // Patient Visit operations (now using patient recordings API)
  async getPatientVisits(patientId?: number): Promise<PatientVisit[]> {
    try {
      if (!patientId) {
        // If no patient ID provided, return empty array since new API requires patient_id
        return [];
      }

      // First get the patient to get the patient_id (PT format)
      const patient = await this.getPatient(patientId);
      if (!patient.patient_id) {
        console.warn('Patient does not have a patient_id, cannot fetch recordings');
        return [];
      }

      const url = `/api/scribing/patients/${patient.patient_id}/recordings?skip=0&limit=50&status_filter=Completed&media_type=audio`;
      
      const response = await axios.get(url, {
        headers: this.getAuthHeaders(),
      });
      
      // Transform recording data to PatientVisit format for backward compatibility
      // The new API returns: { patient: {...}, recordings: [...], pagination: {...} }
      const responseData = response.data;
      const recordings = responseData.recordings || [];
      const patientData = responseData.patient || patient;
      
      return recordings.map((recording: any) => ({
        id: recording.id,
        patient_id: patientId,
        recording_id: recording.id,
        visit_date: recording.created_at,
        visit_type: recording.visit_type || 'Recording Visit',
        chief_complaint: recording.title || 'Audio Recording',
        diagnosis: '',
        treatment_plan: '',
        follow_up_date: null,
        notes: '',
        created_at: recording.created_at,
        updated_at: recording.last_processed || recording.created_at,
        patient: {
          ...patient,
          ...patientData
        },
        recording: {
          id: recording.id,
          title: recording.title || 'Untitled Recording',
          date: recording.created_at,
          status: recording.status?.toLowerCase() || 'completed',
          duration: recording.duration
        }
      }));
    } catch (error) {
      console.error('Error fetching patient recordings:', error);
      throw error;
    }
  }

  // Legacy methods - these are now deprecated since we're using the recordings API
  // Keeping them for backward compatibility but they will return empty/error responses
  
  async getPatientVisit(id: number): Promise<PatientVisit> {
    throw new Error('getPatientVisit is deprecated. Use getPatientVisits with patient ID instead.');
  }

  async createPatientVisit(visitData: PatientVisitFormData): Promise<PatientVisit> {
    throw new Error('createPatientVisit is deprecated. Recordings are now automatically associated with patients.');
  }

  async updatePatientVisit(id: number, visitData: Partial<PatientVisitFormData>): Promise<PatientVisit> {
    throw new Error('updatePatientVisit is deprecated. Use recording update APIs instead.');
  }

  async deletePatientVisit(id: number): Promise<void> {
    throw new Error('deletePatientVisit is deprecated. Use recording delete APIs instead.');
  }

  // Associate a recording with a patient (deprecated - recordings are now automatically associated)
  async associateRecordingWithPatient(recordingId: string, patientId: number, visitData?: Partial<PatientVisitFormData>): Promise<PatientVisit> {
    console.warn('associateRecordingWithPatient is deprecated. Recordings are now automatically associated with patients when created.');
    
    // Return a mock PatientVisit object for backward compatibility
    const patient = await this.getPatient(patientId);
    return {
      id: parseInt(recordingId) || 0,
      patient_id: patientId,
      recording_id: recordingId,
      visit_date: new Date().toISOString(),
      visit_type: 'Recording Visit',
      chief_complaint: 'Auto-associated recording',
      diagnosis: '',
      treatment_plan: '',
      follow_up_date: null,
      notes: '',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      patient: patient,
      recording: {
        id: recordingId,
        title: 'Associated Recording',
        date: new Date().toISOString(),
        status: 'completed',
        duration: 0
      }
    };
  }

  // Get recordings for a specific patient
  async getPatientRecordings(patientId: number): Promise<PatientVisit[]> {
    try {
      return await this.getPatientVisits(patientId);
    } catch (error) {
      console.error('Error fetching patient recordings:', error);
      throw error;
    }
  }

  // Search patients by name
  async searchPatients(query: string): Promise<Patient[]> {
    try {
      const response = await axios.get(`/api/scribing/patients/search/?q=${encodeURIComponent(query)}`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Error searching patients:', error);
      throw error;
    }
  }
}

export const patientService = new PatientService(); 